import { createApiRef } from '@backstage/core-plugin-api';

/**
 * Configuration for a single project within a tribe.
 */
export interface ProjectConfig {
  aliases: string[];
}

/**
 * Configuration for a tribe containing multiple projects.
 */
export interface TribeConfig {
  projects: Record<string, ProjectConfig>;
}

/**
 * Full registry configuration shape.
 */
export interface RegistryConfig {
  version: string;
  default_tribe: string;
  tribes: Record<string, TribeConfig>;
}

/**
 * Metadata about the registry file.
 */
export interface RegistryMeta {
  storagePath: string;
  lastModified: string | null;
}

/**
 * Validation result.
 */
export interface ValidationResult {
  valid: boolean;
  errors: Array<{ field: string; message: string }>;
}

/**
 * DefectDojo product info.
 */
export interface DojoProduct {
  id: number;
  name: string;
  prod_type: number;
}

/**
 * DefectDojo engagement info.
 */
export interface DojoEngagement {
  id: number;
  name: string;
  product: number;
}

/**
 * Merge result.
 */
export interface MergeResult {
  success: boolean;
  message: string;
  movedEngagements: number;
}

/**
 * Notification from cron registration events.
 */
export interface RegistryNotification {
  id: string;
  message: string;
  timestamp: string;
}

/**
 * API surface for the Projects Registry frontend plugin.
 */
export interface ProjectsRegistryApi {
  /** Get the parsed config + metadata */
  getRegistry(): Promise<{ config: RegistryConfig; meta: RegistryMeta }>;

  /** Update the full config (structured) */
  putRegistry(config: RegistryConfig): Promise<void>;

  /** Get the raw YAML string */
  getRegistryRaw(): Promise<{ content: string }>;

  /** Update with raw YAML string */
  putRegistryRaw(content: string): Promise<void>;

  /** Validate YAML content without saving */
  validateRegistry(content: string): Promise<ValidationResult>;

  /** Get file metadata */
  getMeta(): Promise<RegistryMeta>;

  /** List all products from DefectDojo */
  getDojoProducts(): Promise<DojoProduct[]>;

  /** List engagements for a product */
  getDojoEngagements(productId: number): Promise<DojoEngagement[]>;

  /** Merge source product into target product in DefectDojo */
  mergeDojoProducts(sourceProductId: number, targetProductId: number, sourceName: string, targetName: string): Promise<MergeResult>;

  /** Create a new product in DefectDojo (under Default tribe) */
  createDojoProduct(name: string): Promise<DojoProduct>;

  /** Get notifications (paginated) */
  getNotifications(page?: number, pageSize?: number): Promise<{ notifications: RegistryNotification[]; total: number; page: number; pageSize: number }>;

  /** Clear all notifications */
  clearNotifications(): Promise<void>;
}

export const projectsRegistryApiRef = createApiRef<ProjectsRegistryApi>({
  id: 'plugin.projects-registry.api',
});
